import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import {
  AdminBillingSummary,
  AdminSubscription,
  AdminCreditLedgerEntry,
  SUBSCRIPTION_STATUSES,
  SUBSCRIPTION_TIERS,
  usd
} from '@app/Billing/types';
import { getBillingSummary, getSubscriptions, getLedger } from '@app/Billing/billing.api';
import { Pill } from '@app/Campaigns/badges';
import { relativeTime, formatDateTime } from '@app/Screens/format';
import { colors, radii, shadows } from '@app/components/designTokens';

type Tab = 'overview' | 'subscriptions' | 'ledger';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '11px 14px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};
const td: React.CSSProperties = {
  padding: '12px 14px',
  fontSize: 13.5,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
};

const statusTone = (status: string | null): 'green' | 'amber' | 'red' | 'gray' => {
  switch (status) {
    case 'ACTIVE':
      return 'green';
    case 'TRIALING':
      return 'amber';
    case 'PAST_DUE':
    case 'INCOMPLETE':
      return 'red';
    default:
      return 'gray';
  }
};

export const Billing: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');

  const [summary, setSummary] = useState<AdminBillingSummary | null>(null);
  const [subs, setSubs] = useState<AdminSubscription[]>([]);
  const [ledger, setLedger] = useState<AdminCreditLedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');

  const load = useCallback(
    async (spinner: boolean) => {
      if (spinner) setLoading(true);
      try {
        const [sum, subsList, led] = await Promise.all([
          getBillingSummary(),
          getSubscriptions({ status: statusFilter || undefined, tier: tierFilter || undefined, limit: 200 }),
          getLedger(200)
        ]);
        setSummary(sum);
        setSubs(subsList);
        setLedger(led);
        setError(null);
      } catch (e: any) {
        setError(e?.message || 'Failed to load billing');
        if (!spinner) toast.error('Could not refresh billing');
      } finally {
        if (spinner) setLoading(false);
      }
    },
    [statusFilter, tierFilter]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, color: colors.title, margin: 0 },
    subtle: { fontSize: 13, color: colors.subtle },
    refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: radii.button, background: '#fff', color: colors.body, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    tabs: { display: 'flex', gap: 4, borderBottom: `1px solid ${colors.border}`, marginBottom: 16 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 22 },
    card: { background: '#fff', borderRadius: radii.card, boxShadow: shadows.softCard, border: `1px solid ${colors.border}`, overflow: 'hidden' },
    panel: { background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, boxShadow: shadows.softCard, padding: 18, marginBottom: 16 },
    panelTitle: { fontSize: 15, fontWeight: 700, color: colors.title, margin: '0 0 14px' },
    toolbar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
    select: { padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, background: '#fff', color: colors.body }
  };

  const tabBtn = (key: Tab, label: string) => {
    const active = tab === key;
    return (
      <button
        onClick={() => setTab(key)}
        style={{ padding: '10px 16px', border: 'none', background: 'transparent', color: active ? colors.accent : colors.muted, fontSize: 14, fontWeight: 600, cursor: 'pointer', borderBottom: `2px solid ${active ? colors.accent : 'transparent'}`, marginBottom: -1 }}
      >
        {label}
      </button>
    );
  };

  const kpi = (label: string, value: React.ReactNode, sub?: string) => (
    <div style={{ background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, boxShadow: shadows.softCard, padding: '18px 20px' }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: colors.subtle }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color: colors.title, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: colors.subtle, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Billing & Revenue</h1>
          <div style={styles.subtle}>{loading ? 'Loading…' : summary ? `Updated ${relativeTime(summary.generatedAt)}` : ''}</div>
        </div>
        <button style={styles.refreshBtn} onClick={() => load(true)}>
          <SyncIcon className={loading ? 'logo-bolt-pulse' : undefined} /> Refresh
        </button>
      </div>

      <div style={styles.tabs}>
        {tabBtn('overview', 'Overview')}
        {tabBtn('subscriptions', 'Subscriptions')}
        {tabBtn('ledger', 'Credit ledger')}
      </div>

      {error && (
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 12 }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...styles.card, padding: 48, textAlign: 'center', color: colors.subtle }}>Loading billing…</div>
      ) : tab === 'overview' && summary ? (
        <>
          <div style={styles.grid}>
            {kpi('MRR', usd(summary.mrrCents), 'active subscriptions only')}
            {kpi('ARR', usd(summary.mrrCents * 12), 'MRR × 12')}
            {kpi('Subscriptions', summary.subscriptionsTotal, `${summary.subscriptionsByStatus['ACTIVE'] || 0} active · ${summary.subscriptionsByStatus['TRIALING'] || 0} trialing`)}
            {kpi('Credits used', summary.creditsUsedThisPeriod, 'this period (all users)')}
            {kpi('Past due', summary.subscriptionsByStatus['PAST_DUE'] || 0, 'need attention')}
          </div>

          <div style={styles.panel}>
            <h3 style={styles.panelTitle}>Tier breakdown</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                <thead>
                  <tr>
                    <th style={th}>Tier</th>
                    <th style={th}>Price / mo</th>
                    <th style={th}>Total</th>
                    <th style={th}>Active</th>
                    <th style={th}>MRR</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.tiers.length === 0 ? (
                    <tr>
                      <td style={td} colSpan={5}>
                        No tier config found.
                      </td>
                    </tr>
                  ) : (
                    summary.tiers.map(t => (
                      <tr key={t.tier}>
                        <td style={td}>
                          <strong>{t.displayName || t.tier}</strong>
                        </td>
                        <td style={td}>{usd(t.priceCents)}</td>
                        <td style={td}>{t.totalCount}</td>
                        <td style={td}>{t.activeCount}</td>
                        <td style={td}>{usd(t.priceCents * t.activeCount)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : tab === 'subscriptions' ? (
        <>
          <div style={styles.toolbar}>
            <select style={styles.select} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {SUBSCRIPTION_STATUSES.map(s => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <select style={styles.select} value={tierFilter} onChange={e => setTierFilter(e.target.value)}>
              <option value="">All tiers</option>
              {SUBSCRIPTION_TIERS.map(t => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          <div style={styles.card}>
            {subs.length === 0 ? (
              <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>No subscriptions match these filters.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 940 }}>
                  <thead>
                    <tr>
                      <th style={th}>User</th>
                      <th style={th}>Tier</th>
                      <th style={th}>Status</th>
                      <th style={th}>Period ends</th>
                      <th style={th}>Credits</th>
                      <th style={th}>AI ads</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map(s => (
                      <tr
                        key={s.subscriptionId}
                        onClick={() => s.userId != null && navigate(`/users/${s.userId}`)}
                        style={{ cursor: s.userId != null ? 'pointer' : 'default' }}
                      >
                        <td style={td}>
                          <div style={{ fontWeight: 600 }}>{s.userName || '—'}</div>
                          <div style={{ fontSize: 12, color: colors.subtle }}>{s.userEmail}</div>
                        </td>
                        <td style={td}>
                          <strong>{s.tier || '—'}</strong>
                        </td>
                        <td style={td}>
                          {s.status ? <Pill text={s.status} tone={statusTone(s.status)} /> : '—'}
                          {s.cancelAtPeriodEnd && (
                            <span style={{ marginLeft: 6 }}>
                              <Pill text="CANCELING" tone="amber" />
                            </span>
                          )}
                        </td>
                        <td style={td} title={formatDateTime(s.currentPeriodEnd)}>{s.currentPeriodEnd ? relativeTime(s.currentPeriodEnd) : '—'}</td>
                        <td style={td}>{s.creditsUsedThisPeriod}</td>
                        <td style={td}>{s.aiAdsThisPeriod}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : (
        <div style={styles.card}>
          {ledger.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>No credit-ledger activity yet.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                <thead>
                  <tr>
                    <th style={th}>When</th>
                    <th style={th}>User</th>
                    <th style={th}>Reason</th>
                    <th style={th}>Delta</th>
                    <th style={th}>Run</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map(e => {
                    const positive = e.delta > 0;
                    return (
                      <tr key={e.id}>
                        <td style={td} title={formatDateTime(e.createdAt)}>{relativeTime(e.createdAt)}</td>
                        <td
                          style={{ ...td, cursor: e.userId != null ? 'pointer' : 'default', color: e.userId != null ? colors.accent : colors.body }}
                          onClick={() => e.userId != null && navigate(`/users/${e.userId}`)}
                        >
                          {e.userEmail || `user #${e.userId ?? '?'}`}
                        </td>
                        <td style={td}>{e.reason ? e.reason.replace(/_/g, ' ') : '—'}</td>
                        <td style={{ ...td, fontWeight: 700, color: positive ? '#047857' : colors.danger }}>
                          {positive ? `+${e.delta}` : e.delta}
                        </td>
                        <td
                          style={{ ...td, cursor: e.campaignRunId != null ? 'default' : 'default', color: colors.subtle }}
                        >
                          {e.campaignRunId != null ? `run #${e.campaignRunId}` : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
